import moment from "moment-timezone";
import { currentTenantTimezone } from "@/lib/helpers.js";

import type { ReplaceFileUpload, SummaryItem } from "@/DataImports/flow/types";
import {
  dryRunActive,
  formatClock,
  formatDateTime,
  type HubFile,
  type ImportAction,
  type ImportRow,
  plural
} from "@/DataImports/importsHubRows";

export interface DryRunGroup {
  message: string;
  rows: number;
}

export interface DryRun {
  state: "running" | "stopping" | "finished" | "stalled" | "cancelled";
  started_by: string | null;
  started_at: string | null;
  finished_at: string | null;
  problem_row_count: number | null;
  /** Null until the run finishes. */
  warning_count: number | null;
  warning_row_count: number | null;
  groups: DryRunGroup[];
  more_groups: number;
}

export interface BatchFile extends HubFile {
  file_name: string;
}

export interface MappingPair {
  column: string;
  field_label: string;
}

export interface BatchLinks {
  hub: string;
  review_page: string;
  map_page?: string;
  mapping?: string;
  start_load?: string;
  reset_mapping?: string;
  import?: string;
  dry_run_report?: string;
  errors_download?: string;
  replace_file?: string;
  wipe?: string;
  cancel_dry_run?: string;
  cancel_import?: string;
}

// The single-batch JSON: the hub's row shape plus what the Review page reads.
export interface ImportBatch extends Omit<ImportRow, "links"> {
  batch_type: string;
  mappable: boolean;
  can: {
    start_import: boolean;
    dry_run: boolean;
    edit_mapping: boolean;
    wipe: boolean;
    replace_file?: boolean;
  };
  replace_file?: ReplaceFileUpload | null;
  files: BatchFile[];
  columns?: { mapped: number; total: number } | null;
  mapping_summary?: MappingPair[];
  dry_run: DryRun | null;
  links: BatchLinks;
}

// The hub's ••• menu reads a hub row. A row keeps its next step out of `actions` and Review drops
// itself; files and the map link sit elsewhere on the detail payload.
export function menuRowFor(
  batch: ImportBatch,
  hidden: ImportAction[] = []
): ImportRow {
  const skip = new Set<ImportAction | null>([null, "review", ...hidden]);
  const actions = new Set([batch.next_step, ...batch.actions]);
  return {
    ...batch,
    next_step: null,
    actions: [...actions].filter(
      (action): action is ImportAction => !skip.has(action)
    ),
    links: {
      ...batch.links,
      mapping_page: batch.links.map_page,
      files: batch.files
    }
  };
}

export const MAPPING_PREVIEW_COUNT = 4;

export const rowsText = (count: number) => plural(count, "row");
export const problemsSoFarText = (count: number | null) =>
  count ? `, ${plural(count, "problem")} so far` : "";

export const startedAtMs = (run: DryRun | null): number | null =>
  run?.started_at ? Date.parse(run.started_at) : null;

// Dry runs and the row counts that depend on them are only trustworthy once finished.
export const finishedProblems = (batch: ImportBatch): number | null =>
  batch.dry_run?.state === "finished" ? batch.dry_run.problem_row_count : null;

const isLoading = (batch: ImportBatch | null) => batch?.status === "loading";
const isImporting = (batch: ImportBatch | null) =>
  batch?.status === "importing";

export const isPolling = (batch: ImportBatch | null) =>
  isLoading(batch) || isImporting(batch) || dryRunActive(batch?.dry_run?.state);

// A load or dry run is watched closely; an import poll runs a heavy server count, so it goes slower.
const ACTIVE_POLL_MS = 3_000;
const IMPORT_POLL_MS = 10_000;

// null = settled, nothing to poll for.
export function pollIntervalMs(
  batch: ImportBatch | null,
  optimistic: boolean
): number | null {
  if (optimistic || isLoading(batch) || dryRunActive(batch?.dry_run?.state)) {
    return ACTIVE_POLL_MS;
  }
  return isImporting(batch) ? IMPORT_POLL_MS : null;
}

export function runByText(run: DryRun): string {
  const who = run.started_by ? `Run by ${run.started_by}` : "Run";
  if (!run.started_at) return who;
  const at = new Date(run.started_at);
  const today = moment(at)
    .tz(currentTenantTimezone)
    .isSame(moment().tz(currentTenantTimezone), "day");
  return today
    ? `${who} at ${formatClock(at)}`
    : `${who} on ${formatDateTime(run.started_at)}`;
}

function fileLabel(files: BatchFile[]): string {
  return files.length > 1
    ? `${files.length} files`
    : (files[0]?.file_name ?? "");
}

export function stripItems(batch: ImportBatch): SummaryItem[] {
  const problems = finishedProblems(batch);
  const items: SummaryItem[] = [
    { label: "File", value: fileLabel(batch.files) },
    { label: "Rows loaded", value: batch.rows }
  ];
  if (batch.mappable && batch.columns) {
    items.push({
      label: "Columns mapped",
      value: `${batch.columns.mapped} of ${batch.columns.total}`
    });
  }
  items.push({
    label: "Rows with problems",
    value: problems,
    tone: problems ? "danger" : undefined
  });
  return items;
}
